import { App, Alert, List, Modal, Skeleton, Tabs } from "antd"
import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { ROLE_LABEL_STATUSES } from "../../../constants/role-labels.ts"
import { getDuplicateCode, getDuplicateName } from "../helpers/utils.ts"
import type { RoleDeleteBlockingResponse } from "../redux/permissions.types.ts"
import type {
  PermissionLabel,
  PermissionLabelFormData
} from "../types/permissions.types.ts"
import { PermissionsLabelController } from "./PermissionsLabelController.tsx"
import { PermissionsLabelsItem } from "./PermissionsLabelsItem.tsx"

const PermissionsLabelModal = lazy(() =>
  import("./PermissionsLabelModal.tsx").then((m) => ({
    default: m.PermissionsLabelModal
  }))
)

interface PermissionsLabelsProps {
  activeLabel: PermissionLabel | null
  setActiveLabel: (label: PermissionLabel) => void
  getLabels: () => Promise<PermissionLabel[]>
  onAddLabel: (
    data: PermissionLabelFormData
  ) => Promise<PermissionLabel> | undefined
  onUpdateLabel: (
    id: number,
    data: PermissionLabelFormData
  ) => Promise<PermissionLabel | undefined>
  onDeleteLabel: (id: number) => Promise<void>
  onDuplicateLabel: (
    item: PermissionLabel
  ) => Promise<PermissionLabel | undefined>
}

export function PermissionsLabels({
  setActiveLabel,
  activeLabel,
  getLabels,
  onAddLabel,
  onUpdateLabel,
  onDeleteLabel,
  onDuplicateLabel
}: PermissionsLabelsProps) {
  const { t } = useTranslation()
  const { modal } = App.useApp()
  const [labels, setLabels] = useState<PermissionLabel[]>([])
  const [loading, setLoading] = useState(true)
  const [editable, setEditable] = useState<PermissionLabel | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<PermissionLabel | null>(null)
  const [deleteBlocking, setDeleteBlocking] =
    useState<RoleDeleteBlockingResponse | null>(null)
  const [deleting, setDeleting] = useState(false)

  const handleGetLabels = async () => {
    try {
      setLoading(true)
      const items = await getLabels()
      setLabels(items)
    } finally {
      setLoading(false)
    }
  }

  const sortByName = (items: PermissionLabel[]) =>
    [...items].sort((a, b) => {
      if (a.code === ROLE_LABEL_STATUSES.OWNER) return -1
      if (b.code === ROLE_LABEL_STATUSES.OWNER) return 1
      return a?.name?.toLowerCase() > b?.name?.toLowerCase() ? 1 : -1
    })

  const handleAddLabel = async (data: PermissionLabelFormData) => {
    const createdLabel = await onAddLabel(data)

    if (createdLabel) {
      setLabels((prev) => sortByName([...prev, createdLabel]))
      setActiveLabel(createdLabel)
    }
  }

  const handleDuplicateLabel = async (data: PermissionLabel) => {
    const onOk = async () => {
      const createdLabel = await onDuplicateLabel({
        ...data,
        name: getDuplicateName(data.name, labels),
        code: getDuplicateCode(data.code, labels)
      })

      if (createdLabel) {
        setLabels((prev) => sortByName([...prev, createdLabel]))
        setActiveLabel(createdLabel)
      }
    }
    modal.confirm({
      title: "Duplicate Role",
      content: "Are you sure you want to duplacate this role?",
      okText: "Duplicate",
      okType: "primary",
      cancelText: "Cancel",
      onOk: onOk
    })
  }

  const handleUpdateLabel = async (data: PermissionLabelFormData) => {
    if (!editable?.id) return
    const updatedLabel = await onUpdateLabel(editable.id, {
      ...editable,
      ...data
    })

    if (updatedLabel) {
      setLabels((prev) =>
        sortByName(
          prev.map((l) => (l.id === updatedLabel.id ? updatedLabel : l))
        )
      )
    }
  }

  // A controlled Modal rather than modal.confirm: antd closes a confirm as soon
  // as onOk resolves, so a rejected delete could not both keep the dialog open
  // and report which users still hold the role.
  const handleDeleteLabel = useCallback(
    (id: number) => {
      setDeleteBlocking(null)
      setDeleteTarget(labels.find((l) => l.id === id) ?? null)
    },
    [labels]
  )

  const handleCloseDeleteModal = () => {
    setDeleteTarget(null)
    setDeleteBlocking(null)
  }

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return

    setDeleting(true)
    try {
      await onDeleteLabel(deleteTarget.id)

      const remaining = labels.filter((l) => l.id !== deleteTarget.id)
      setLabels(remaining)
      if (deleteTarget.id === activeLabel?.id) {
        const lastLabel = remaining.at(-1)
        if (lastLabel) setActiveLabel(lastLabel)
      }
      handleCloseDeleteModal()
    } catch (e) {
      const blocking = (e as { blocking?: RoleDeleteBlockingResponse })?.blocking
      if (blocking?.users?.length) {
        setDeleteBlocking(blocking)
        if (!blocking.has_more_users) {
          const freshCount = blocking.users.length
          setLabels((prev) =>
            prev.map((l) =>
              l.id === deleteTarget.id ? { ...l, user_count: freshCount } : l
            )
          )
        }
      } else handleCloseDeleteModal()
    } finally {
      setDeleting(false)
    }
  }

  const labelTabs = useMemo(
    () =>
      labels?.map((l) => ({
        key: l?.id?.toString(),
        label: (
          <PermissionsLabelsItem
            item={l}
            onEdit={setEditable}
            onDelete={handleDeleteLabel}
            onDuplicate={handleDuplicateLabel}
          />
        )
      })),
    [labels, handleDeleteLabel]
  )

  useEffect(() => {
    void handleGetLabels()
  }, [])

  // Above 50 the rejection's user list is truncated server-side, so only the
  // label's own count can report the true total.
  const blockedUserCount = deleteBlocking?.has_more_users
    ? (deleteTarget?.user_count || deleteBlocking.users.length)
    : (deleteBlocking?.users.length ?? 0)

  if (loading) return <Skeleton.Node active className={"h-[42px] w-full"} />

  return (
    <>
      {editable && (
        <Suspense fallback={null}>
          <PermissionsLabelModal
            open
            isEdit
            initialValues={{
              name: editable?.name,
              code: editable?.code
            }}
            onClose={() => setEditable(null)}
            onSubmit={handleUpdateLabel}
            existingNames={labels
              .filter((l) => l.id !== editable.id)
              .map((l) => l.name)}
          />
        </Suspense>
      )}

      <Modal
        open={!!deleteTarget}
        title="Delete Role"
        okText="Delete"
        okType="danger"
        cancelText="Cancel"
        onOk={handleConfirmDelete}
        onCancel={handleCloseDeleteModal}
        confirmLoading={deleting}
        okButtonProps={{ disabled: !!deleteBlocking }}
        maskClosable={!deleting}
        destroyOnHidden
      >
        {deleteBlocking ? (
          <Alert
            type="warning"
            showIcon
            message={t("permissions.deleteBlockedTitle")}
            description={
              <>
                <p className="mb-2">
                  {t("permissions.deleteBlockedDescription")}
                </p>
                <List
                  size="small"
                  dataSource={deleteBlocking.users}
                  renderItem={(user) => (
                    <List.Item key={user.user_id} className="px-0">
                      <List.Item.Meta
                        title={user.user_full_name}
                        description={user.user_email}
                      />
                    </List.Item>
                  )}
                />
                {deleteBlocking.has_more_users && (
                  <p className="mt-2 mb-0 opacity-70">
                    {t("permissions.deleteBlockedMore", {
                      count: deleteBlocking.users.length
                    })}
                  </p>
                )}
                {deleteTarget && (
                  <p className="mt-3 mb-0">
                    <Link
                      to={`/team_management?labels=${deleteTarget.id}`}
                      onClick={handleCloseDeleteModal}
                    >
                      {t("permissions.deleteBlockedViewUsers", {
                        count: blockedUserCount
                      })}
                    </Link>
                  </p>
                )}
              </>
            }
          />
        ) : (
          "Are you sure you want to delete this role?"
        )}
      </Modal>

      <Tabs
        className={
          "[&_.ant-tabs-tab]:p-0 [&_.ant-tabs-nav]:mb-0 [&_.ant-tabs-nav-wrap]:flex-[0_1_auto]"
        }
        type="card"
        activeKey={activeLabel?.id?.toString() || ""}
        onChange={(id) => {
          const label = labels?.find((l) => l?.id?.toString() === id)
          if (label) setActiveLabel(label)
        }}
        tabBarExtraContent={{
          right: (
            <PermissionsLabelController
              onAddLabel={handleAddLabel}
              existingNames={labels.map((l) => l.name)}
            />
          )
        }}
        items={labelTabs}
      />
    </>
  )
}
